import React, { useState, useContext, useEffect } from 'react';
import {
  BankOutlined,
  HomeOutlined,
  StarOutlined,
  EnvironmentOutlined,
  WarningOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import dayjs from "dayjs";

const user_avatar = "https://api.dicebear.com/7.x/thumbs/svg?seed=user";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: #f0f2f5;
    min-height: 100vh;
    padding: 32px 16px;
  }

  .log-wrapper {
    max-width: 780px;
    margin: 0 auto;
  }

  .log-header {
    margin-bottom: 32px;
  }

  .log-header h2 {
    font-size: 22px;
    font-weight: 600;
    color: #1a1a2e;
    letter-spacing: -0.3px;
  }

  .log-header p {
    font-size: 13px;
    color: #888;
    margin-top: 4px;
    font-family: 'DM Mono', monospace;
  }

  .timeline {
    position: relative;
    padding-left: 0;
  }

  .timeline::before {
    display: none;
  }

  .timeline-item {
    position: relative;
    margin-bottom: 14px;
    animation: fadeUp 0.4s ease both;
  }

  .timeline-item:nth-child(1) { animation-delay: 0.05s; }
  .timeline-item:nth-child(2) { animation-delay: 0.15s; }
  .timeline-item:nth-child(3) { animation-delay: 0.25s; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .dot {
    display: none;
  }

  .card {
    background: #ffffff;
    border-radius: 16px;
    padding: 16px 18px;
    box-shadow: 0 4px 16px rgba(15,23,42,0.04);
    border: 1px solid #e2e8f0;
    transition: box-shadow 0.2s;
  }

  .card:hover {
    box-shadow: 0 4px 20px rgba(0,0,0,0.09);
  }

  .card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .location-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .location-icon {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .type-tag {
    font-size: 11px;
    font-weight: 500;
    font-family: 'DM Mono', monospace;
    padding: 3px 10px;
    border-radius: 20px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .type-tag.check { background: #eef0ff; color: #0e497b; }
  .type-tag.punch { background: #f3eeff; color: #0e497b; }

  .times-row {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    margin-bottom: 12px;
  }

  .time-block {
    flex: 1;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 10px 14px;
  }

  .time-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  .time-value {
    font-size: 17px;
    font-weight: 800;
    color: #0f172a;
    font-family: 'DM Mono', monospace;
  }

  .time-value.missing {
    color: #f59e0b;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .photos-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .photos-section-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #aaa;
    margin-right: 2px;
  }

  .photo-block {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e8e8e8;
    width: 100px;
    height: 100px;
    flex-shrink: 0;
  }

  .photo-block img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .photo-label {
    position: absolute;
    bottom: 3px;
    left: 3px;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(0,0,0,0.5);
    color: white;
    padding: 1px 5px;
    border-radius: 20px;
    line-height: 1.6;
  }

  .no-photo {
    width: 48px;
    height: 48px;
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    color: #bbb;
    font-size: 9px;
    flex-shrink: 0;
  }

  .punch-divider {
    height: 1px;
    background: #f0f0f0;
    margin: 12px 0;
  }
`;

function getEntryType(punch) {
  return punch?.PunchIn || punch?.PunchOut ? 'punch' : 'check';
}

function getInTime(punch, type) {
  return type === "punch" ? punch?.PunchIn : punch?.CheckIn;
}

function getOutTime(punch, type) {
  return type === "punch" ? punch?.PunchOut : punch?.CheckOut;
}

const locationConfig = {
  "Head Office": { Icon: BankOutlined, bg: "#eef0ff", color: "#0e497b" },
  "Crown Residencies": { Icon: HomeOutlined, bg: "#f3eeff", color: "#0e497b" },
  "Enchanted": { Icon: StarOutlined, bg: "#fff7e6", color: "#0e497b" },
};


const calculateTotalHours = (inTimeStr, outTimeStr) => {
  if (!inTimeStr || !outTimeStr) return null;
  const parseTime = (str) => {
    const parts = String(str).split(":").map(Number);
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    return parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0);
  };
  const startSec = parseTime(inTimeStr);
  const endSec = parseTime(outTimeStr);
  if (startSec === null || endSec === null || endSec < startSec) return null;

  const diffSec = endSec - startSec;
  const hrs = Math.floor(diffSec / 3600);
  const mins = Math.floor((diffSec % 3600) / 60);
  return `${hrs}h ${mins}m`;
};

export default function ImproperLog({ record, employee }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const { authData, backUrl } = useContext(AuthContext);
  const [showLead, setShowLead] = useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);
  // Stores resolved location names keyed by "lat,lng"
  const [locationNames, setLocationNames] = useState({});

  const today = dayjs().format("YYYY-MM-DD");

  console.log("RECORD  :", record)
  console.log("Employee :", employee)

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ date: record.Date });
      if (employee?.id) params.append('employee_id', employee.id);

      const res = await axios.get(
        `${backUrl}/api/hr/attendance-comparsion/?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setAttendanceData(res.data.results || []);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setAttendanceData([]);
    }
  };

  // Reverse-geocode coordinates to a human-readable location name
  const fetchLocationName = async (lat, lng) => {
    const key = `${lat},${lng}`;
    if (locationNames[key]) return; // already fetched
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      // Use suburb/neighbourhood/town for a short name, fallback to full display_name
      const addr = data.address || {};
      const shortName =
        addr.suburb ||
        addr.neighbourhood ||
        addr.village ||
        addr.town ||
        addr.city ||
        addr.county ||
        data.display_name ||
        `${lat}, ${lng}`;
      setLocationNames((prev) => ({ ...prev, [key]: shortName }));
    } catch {
      setLocationNames((prev) => ({ ...prev, [key]: `${lat}, ${lng}` }));
    }
  };

  useEffect(() => {
    fetchData()
  }, [])

  // Fetch location names for all auto-checkout entries after data loads
  useEffect(() => {
    attendanceData.forEach((entry) => {
      const lat = entry?.sales_checkout_latitude;
      const lng = entry?.sales_checkout_longitude;
      if (lat && lng) fetchLocationName(lat, lng);

      const punchDetails = entry?.punch_details || [];
      punchDetails.forEach((punch) => {
        const plat = punch?.sales_checkout_latitude;
        const plng = punch?.sales_checkout_longitude;
        if (plat && plng) fetchLocationName(plat, plng);
      });
    });
  }, [attendanceData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style>{styles}</style>
      <div className="log-wrapper">
        <div className="timeline">
          {attendanceData.map((entry, entryIdx) => {
            const punchDetails = entry?.punch_details || [];
            const sales_remarks = entry?.sales_checkout_remarks || null;

            return punchDetails.map((punch, punchIdx) => {
              const type = getEntryType(punch);
              const inTime = getInTime(punch, type);
              const outTime = getOutTime(punch, type);
              const locConf = locationConfig[entry.WorkLocation] || { Icon: EnvironmentOutlined, bg: "#f0f0f0", color: "#888" };
              const { Icon } = locConf;
              const autoCheckoutRemarks = punch?.sales_checkout_remarks || null;
              const autoLat = punch?.sales_checkout_latitude || null;
              const autoLng = punch?.sales_checkout_longitude || null;

              return (
                <div className="timeline-item" key={`${entryIdx}-${punchIdx}`}>
                  <div className={`dot ${type}`} />
                  <div className="card">
                    <div className="card-top">
                      <div className="location-badge">
                        {punch.check_in_work_site && punch.check_out_work_site ? (
                          `${punch.check_in_work_site} - ${punch.check_out_work_site}`
                        ) : punch.check_in_work_site ? (
                          punch.check_in_work_site
                        ) : punch.check_out_work_site ? (
                          punch.check_out_work_site
                        ) : entry.WorkLocation}
                      </div>
                    </div>

                    {inTime && (
                      <div style={{ marginTop: 8, marginBottom: 16 }}>
                        {/* Top Location Bar */}
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                          <span>📍</span>
                          <span>
                            {punch.check_in_work_site && punch.check_out_work_site ? (
                              `${punch.check_in_work_site} ➔ ${punch.check_out_work_site}`
                            ) : punch.check_in_work_site ? (
                              punch.check_in_work_site
                            ) : punch.check_out_work_site ? (
                              punch.check_out_work_site
                            ) : (entry.WorkLocation || "Head Office")}
                          </span>
                        </div>

                        {/* Punch In (Green Card) <--- Connecting Timeline Line + Total Hours ---> Punch Out (Blue Card) */}
                        <div style={{ display: "flex", alignItems: "center", gap: 0, position: "relative" }}>
                          {/* GREEN PUNCH IN CARD */}
                          <div
                            style={{
                              flex: 1,
                              background: "#f0fdf4",
                              border: "1.5px solid #bbf7d0",
                              borderRadius: 14,
                              padding: "14px 18px",
                              boxShadow: "0 2px 8px rgba(16, 185, 129, 0.06)",
                              zIndex: 2,
                            }}
                          >
                            <div style={{ fontSize: 11, fontWeight: 800, color: "#047857", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981", display: "inline-block" }} />
                              {type === 'check' ? 'CHECK IN' : 'PUNCH IN'}
                            </div>
                            <div style={{ fontSize: 19, fontWeight: 900, color: "#065f46", fontFamily: "'Inter', sans-serif" }}>
                              {inTime}
                            </div>
                          </div>

                          {/* IN-BETWEEN TIMELINE LINE WITH TOTAL HOURS BADGE */}
                          {inTime && outTime && (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 130, margin: "0 -6px", zIndex: 3, position: "relative" }}>
                              {/* Horizontal Timeline Line */}
                              <div
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  right: 0,
                                  height: 3,
                                  background: "linear-gradient(90deg, #10b981 0%, #3b82f6 100%)",
                                  zIndex: 1,
                                }}
                              />
                              {/* Total Hours Badge Pill in Center */}
                              <div
                                style={{
                                  position: "relative",
                                  zIndex: 2,
                                  background: "#ffffff",
                                  border: "2px solid #94a3b8",
                                  borderRadius: 22,
                                  padding: "6px 16px",
                                  fontSize: 14,
                                  fontWeight: 900,
                                  color: "#0f172a",
                                  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
                                  whiteSpace: "nowrap",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <span style={{ fontSize: 15 }}>⏱️</span>
                                <span style={{ letterSpacing: "-0.3px" }}>{calculateTotalHours(inTime, outTime) || "--"}</span>
                              </div>
                            </div>
                          )}

                          {/* BLUE PUNCH OUT CARD */}
                          <div
                            style={{
                              flex: 1,
                              background: "#eff6ff",
                              border: "1.5px solid #bfdbfe",
                              borderRadius: 14,
                              padding: "14px 18px",
                              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.06)",
                              zIndex: 2,
                            }}
                          >
                            <div style={{ fontSize: 11, fontWeight: 800, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3b82f6", display: "inline-block" }} />
                              {type === 'check' ? 'CHECK OUT' : 'PUNCH OUT'}
                            </div>
                            {outTime ? (
                              <div style={{ fontSize: 19, fontWeight: 900, color: "#1e40af", fontFamily: "'Inter', sans-serif" }}>
                                {outTime}
                              </div>
                            ) : (
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#d97706" }}>--:--</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {(() => {
                      const raw = punch.lead_data || punch.lead_details || punch.external_lead_details;
                      if (!raw) return false;
                      try {
                        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                        if (Array.isArray(parsed)) return parsed.length > 0;
                        if (typeof parsed === "object" && parsed !== null) return Object.keys(parsed).length > 0;
                        return !!parsed;
                      } catch (e) {
                        return typeof raw === "string" && raw.trim() !== "" && raw !== "[]" && raw !== "{}";
                      }
                    })() && (
                        <div style={{ marginBottom: 14 }}>
                          <a
                            onClick={() => {
                              const raw = punch.lead_data || punch.lead_details || punch.external_lead_details;
                              let parsed = raw;
                              if (typeof raw === "string") { try { parsed = JSON.parse(raw); } catch { } }
                              const arr = Array.isArray(parsed) ? parsed : [parsed];
                              setSelectedLeadDetails(arr);
                              setShowLead(true);
                            }}
                            style={{
                              color: "#0e497b",
                              fontSize: "12px",
                              fontWeight: 600,
                              textDecoration: "underline",
                              cursor: "pointer"
                            }}
                          >
                            View Visit Lead Details
                          </a>
                        </div>
                      )}

                    {type === 'check' && (
                      <>
                        <div className="photos-section">
                          <span className="photos-section-label">Photos</span>

                          {punch.check_in_photo_url ? (
                            <div className="photo-block">
                              <img src={punch.check_in_photo_url} alt="Check-in" />
                              <span className="photo-label">In</span>
                            </div>
                          ) : (
                            <div className="no-photo">
                              <CameraOutlined style={{ fontSize: 13 }} />
                              <span>In</span>
                            </div>
                          )}

                          {punch.check_out_photo_url ? (
                            <div className="photo-block">
                              <img src={punch.check_out_photo_url} alt="Check-out" />
                              <span className="photo-label">Out</span>
                            </div>
                          ) : (
                            <div className="no-photo">
                              <CameraOutlined style={{ fontSize: 13 }} />
                              <span>Out</span>
                            </div>
                          )}
                        </div>

                        {/* Auto-Checkout Alert — only rendered when auto-checkout occurred */}
                        {autoCheckoutRemarks && (
                          <div style={{
                            marginTop: 12,
                            padding: "8px 12px",
                            background: "#fff7e6",
                            border: "1px solid #ffa940",
                            borderRadius: 8,
                            fontSize: 12,
                            color: "#d46b08",
                            fontWeight: 500,
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}>
                            <span>⚠️ {autoCheckoutRemarks}</span>
                            {autoLat && autoLng && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${autoLat},${autoLng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#1677ff", fontSize: 11, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}
                              >
                                <EnvironmentOutlined style={{ marginRight: 3 }} />
                                {locationNames[`${autoLat},${autoLng}`] || "Fetching location..."}
                              </a>
                            )}
                          </div>
                        )}
                      </>
                    )}


                  </div>
                </div>
              );
            });
          })}

        </div>
      </div>

      {showLead && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '600px',
            maxHeight: '80vh', overflowY: 'auto', padding: '24px', position: 'relative'
          }}>
            <button
              onClick={() => setShowLead(false)}
              style={{
                position: 'absolute', top: '16px', right: '16px', border: 'none',
                background: 'none', cursor: 'pointer', fontSize: '20px', color: '#888'
              }}
            >
              ×
            </button>
            <h3 style={{ marginBottom: '20px', color: '#1a1a2e' }}>Lead Details</h3>
            {selectedLeadDetails && selectedLeadDetails.map((lead, index) => (
              <div key={index} style={{
                marginBottom: '16px', padding: '16px', borderRadius: '8px',
                border: '1px solid #f0f0f0', backgroundColor: '#fafafa'
              }}>
                {selectedLeadDetails.length > 1 && <h4 style={{ marginBottom: '12px' }}>Lead {index + 1}</h4>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', rowGap: '10px', fontSize: '14px' }}>
                  <div style={{ color: '#888' }}>Lead No:</div>
                  <div style={{ fontWeight: 600 }}>{lead.lead_no || lead.lead_id || "-"}</div>
                  <div style={{ color: '#888' }}>Customer:</div>
                  <div>{lead.customer_name || lead.name || "-"}</div>
                  <div style={{ color: '#888' }}>Email:</div>
                  <div>{lead.email || lead.customer_email || "-"}</div>
                  <div style={{ color: '#888' }}>Phone:</div>
                  <div>{lead.number || lead.phone || "-"}</div>
                  <div style={{ color: '#888' }}>Project:</div>
                  <div>{lead.project_name || lead.project_id || (lead.project && lead.project.title) || "-"}</div>
                  <div style={{ color: '#888' }}>Status:</div>
                  <div><span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '12px',
                    backgroundColor: '#eef0ff', color: '#0e497b', fontWeight: 500
                  }}>
                    {lead.status?.title || lead.status || "-"}
                  </span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}